import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { generateConfig } from "../../utils/api";
import { handleApiError } from "../../utils/apiErrorHandler";

// Create a new treatment plan
export const createTreatmentPlan = createAsyncThunk(
  "treatmentPlan/create",
  async (treatmentPlanData, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Treatment Plan Thunk] Creating treatment plan for client: ${treatmentPlanData.clientName}`);

      const response = await axios.post(
        `${baseURL}/api/treatment-plan`,
        treatmentPlanData,
        config
      );

      console.log(`[Treatment Plan Thunk] Successfully created treatment plan:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`[Treatment Plan Thunk] Error creating treatment plan:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Fetch all treatment plans
export const fetchTreatmentPlans = createAsyncThunk(
  "treatmentPlan/fetchAll",
  async (params = {}, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Treatment Plan Thunk] Fetching treatment plans with params:`, params);

      const queryParams = new URLSearchParams();
      if (params.clientId) queryParams.append('clientId', params.clientId);
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);

      const response = await axios.get(
        `${baseURL}/api/treatment-plan?${queryParams.toString()}`,
        config
      );

      console.log(`[Treatment Plan Thunk] Successfully fetched ${response.data.docs?.length || 0} treatment plans`);
      return response.data;
    } catch (error) {
      console.error(`[Treatment Plan Thunk] Error fetching treatment plans:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Fetch a specific treatment plan by ID
export const fetchTreatmentPlanById = createAsyncThunk(
  "treatmentPlan/fetchById",
  async (treatmentPlanId, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Treatment Plan Thunk] Fetching treatment plan: ${treatmentPlanId}`);

      const response = await axios.get(
        `${baseURL}/api/treatment-plan/${treatmentPlanId}`,
        config
      );

      console.log(`[Treatment Plan Thunk] Successfully fetched treatment plan:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`[Treatment Plan Thunk] Error fetching treatment plan:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Update a treatment plan
export const updateTreatmentPlan = createAsyncThunk(
  "treatmentPlan/update",
  async ({ treatmentPlanId, treatmentPlanData }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Treatment Plan Thunk] Updating treatment plan: ${treatmentPlanId}`);

      const response = await axios.put(
        `${baseURL}/api/treatment-plan/${treatmentPlanId}`,
        treatmentPlanData,
        config
      );

      console.log(`[Treatment Plan Thunk] Successfully updated treatment plan:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`[Treatment Plan Thunk] Error updating treatment plan:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Delete a treatment plan
export const deleteTreatmentPlan = createAsyncThunk(
  "treatmentPlan/delete",
  async (treatmentPlanId, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Treatment Plan Thunk] Deleting treatment plan: ${treatmentPlanId}`);

      const response = await axios.delete(
        `${baseURL}/api/treatment-plan/${treatmentPlanId}`,
        config
      );

      console.log(`[Treatment Plan Thunk] Successfully deleted treatment plan`);
      return { treatmentPlanId, message: response.data.message };
    } catch (error) {
      console.error(`[Treatment Plan Thunk] Error deleting treatment plan:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Fetch treatment plans for a specific client
export const fetchClientTreatmentPlans = createAsyncThunk(
  "treatmentPlan/fetchByClient",
  async ({ clientId, page = 1, limit = 10 }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Treatment Plan Thunk] Fetching treatment plans for client: ${clientId}`);

      const response = await axios.get(
        `${baseURL}/api/treatment-plan/client/${clientId}?page=${page}&limit=${limit}`,
        config
      );

      console.log(`[Treatment Plan Thunk] Successfully fetched client treatment plans:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`[Treatment Plan Thunk] Error fetching client treatment plans:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Fetch latest treatment plan for a client (for auto-loading goals in progress summary)
export const fetchLatestTreatmentPlan = createAsyncThunk(
  "treatmentPlan/fetchLatest",
  async (clientId, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Treatment Plan Thunk] Fetching latest treatment plan for client: ${clientId}`);

      const response = await axios.get(
        `${baseURL}/api/treatment-plan/client/${clientId}/latest`,
        config
      );

      console.log(`[Treatment Plan Thunk] Successfully fetched latest treatment plan:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`[Treatment Plan Thunk] Error fetching latest treatment plan:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Autosave treatment plan (create or update as draft)
export const autosaveTreatmentPlan = createAsyncThunk(
  "treatmentPlan/autosave",
  async ({ treatmentPlanId, treatmentPlanData }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      // Add draft status and autosave flag
      const dataWithDraftStatus = {
        ...treatmentPlanData,
        status: 'draft',
        isAutosave: true
      };

      let response;

      if (treatmentPlanId) {
        // Update existing draft
        console.log(`[Treatment Plan Thunk] Autosaving existing treatment plan: ${treatmentPlanId}`);
        response = await axios.put(
          `${baseURL}/api/treatment-plan/${treatmentPlanId}`,
          dataWithDraftStatus,
          config
        );
      } else {
        // Create new draft
        console.log(`[Treatment Plan Thunk] Creating new draft treatment plan`);
        response = await axios.post(
          `${baseURL}/api/treatment-plan`,
          dataWithDraftStatus,
          config
        );
      }

      console.log(`[Treatment Plan Thunk] Autosave successful`);
      return response.data;
    } catch (error) {
      console.error(`[Treatment Plan Thunk] Error autosaving treatment plan:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Fetch user's draft treatment plans
export const fetchMyDraftTreatmentPlans = createAsyncThunk(
  "treatmentPlan/fetchMyDrafts",
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Treatment Plan Thunk] Fetching user's draft treatment plans`);

      const response = await axios.get(
        `${baseURL}/api/treatment-plan/drafts/my`,
        config
      );

      console.log(`[Treatment Plan Thunk] Found ${response.data.count} drafts`);
      return response.data;
    } catch (error) {
      console.error(`[Treatment Plan Thunk] Error fetching drafts:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);
